import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";

const AUDIENCES = ["Vocational rehabilitation clients", "Transition / Pre-ETS students", "Youth ages 14–22", "Families and caregivers", "VR professionals", "Educators / schools", "College or university", "Center for Independent Living", "Community provider", "Employer", "Other"];
const INTERESTS = ["JEM + GVRA Disability Empowerment Pilot", "Documentary screening", "Workshop", "Speaking engagement / keynote", "Regional or statewide partnership", "Annual partnership", "Employer or staff training", "Not sure yet"];
const EMPTY = { name: "", organization: "", email: "", phone: "", audience: "", interest: "" };
const REQUIRED = ["name", "organization", "email", "audience", "interest"];

const inputCls = "mt-2 w-full rounded-xl border border-[#D4AF37]/35 bg-[#12141C] px-4 py-3.5 text-base text-[#F9F8F6] placeholder:text-[#A9A598] transition-colors focus:border-[#F3C649]";

const Field = ({ id, label, error, optional, children }) => (
  <div>
    <label htmlFor={id} className="text-sm font-semibold text-[#F9F8F6]">
      {label} {optional ? <span className="font-normal text-[#C8C5BB]">(optional)</span> : <span aria-hidden="true" className="text-[#F3C649]">*</span>}
    </label>
    {children}
    {error && <p id={`${id}-error`} className="mt-2 text-sm text-[#FF9C8A]" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

const validate = (v) => {
  const e = {};
  REQUIRED.forEach((k) => { if (!v[k].trim()) e[k] = "This field is required."; });
  if (v.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.email)) e.email = "Enter a valid email address.";
  return e;
};

export const InquiryForm = () => {
  const [v, setV] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const bind = (k) => ({
    id: k, name: k, value: v[k], "data-testid": `contact-${k}-input`,
    "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `${k}-error` : undefined,
    onChange: (e) => setV({ ...v, [k]: e.target.value }),
  });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(v);
    setErrors(errs);
    if (Object.keys(errs).length) { document.getElementById(Object.keys(errs)[0])?.focus(); return; }
    setStatus("sending");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries`, { ...v, phone: v.phone || null });
      setStatus("sent");
      toast.success("Thank you — your inquiry was sent.");
    } catch {
      setStatus("idle");
      toast.error(`Something went wrong. Please email ${"jem.partnerships@gmail.com"} directly.`);
    }
  };

  if (status === "sent") {
    return (
      <div role="status" className="rounded-3xl border border-[#F3C649]/50 bg-[#12141C] p-10 sm:p-14" data-testid="contact-success">
        <CheckCircle2 size={40} aria-hidden="true" className="text-[#F3C649]" />
        <h2 className="mt-6 font-display text-4xl font-semibold">Thank you, {v.name.split(" ")[0]}.</h2>
        <p className="mt-4 max-w-lg text-lg text-[#C8C5BB]">Your inquiry is in, and a confirmation is on its way to your inbox. John will follow up soon to discuss a JEM partnership.</p>
        <button type="button" className="btn-ghost mt-8" onClick={() => { setV(EMPTY); setStatus("idle"); }} data-testid="contact-send-another">Send another inquiry</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate aria-label="Partnership inquiry" className="rounded-3xl border border-[#D4AF37]/25 bg-[#0E1016] p-6 sm:p-10" data-testid="contact-form">
      <p className="mb-8 text-sm text-[#C8C5BB]">Fields marked <span className="text-[#F3C649]">*</span> are required.</p>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field id="name" label="Name" error={errors.name}><input type="text" autoComplete="name" className={inputCls} {...bind("name")} /></Field>
        <Field id="organization" label="Organization" error={errors.organization}><input type="text" autoComplete="organization" className={inputCls} {...bind("organization")} /></Field>
        <Field id="email" label="Email" error={errors.email}><input type="email" autoComplete="email" className={inputCls} {...bind("email")} /></Field>
        <Field id="phone" label="Phone" optional><input type="tel" autoComplete="tel" className={inputCls} {...bind("phone")} /></Field>
        <Field id="audience" label="Audience" error={errors.audience}>
          <select className={inputCls} {...bind("audience")}>
            <option value="">Select an audience</option>
            {AUDIENCES.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </Field>
        <Field id="interest" label="Partnership interest" error={errors.interest}>
          <select className={inputCls} {...bind("interest")}>
            <option value="">Select an interest</option>
            {INTERESTS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </Field>
      </div>
      <button type="submit" className="btn-gold mt-10 disabled:opacity-70" disabled={status === "sending"} data-testid="contact-submit-btn">
        {status === "sending" ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : null}
        Discuss a JEM Partnership {status !== "sending" && <ArrowUpRight size={18} aria-hidden="true" />}
      </button>
    </form>
  );
};
